import React from 'react';
import { getKanjiVariants } from './kanjiVariants';
import { isKanjiCategory } from './kanjiCategory';
import SpectrumBars from './SpectrumBars';

const ROMAJIDESU_KANJI_BASE = 'https://www.romajidesu.com/kanji/';

/** English Wiktionary: broad coverage for Russian, Chinese, Spanish; includes IPA / pronunciation and definitions. */
const EN_WIKTIONARY_BASE = 'https://en.wiktionary.org/wiki/';

function englishWiktionaryUrl(foreign) {
  if (!foreign || typeof foreign !== 'string') return null;
  const title = foreign.trim().replace(/\s+/g, '_');
  if (!title) return null;
  return `${EN_WIKTIONARY_BASE}${encodeURIComponent(title)}`;
}

const WIKTIONARY_LOOKUP_LANGUAGES = new Set(['russian', 'chinese', 'spanish']);

const Flashcard = ({
  word,
  currentLanguage = '',
  activeKanjiVariantIndex = 0,
  onCycleKanjiVariant,
  scoreFlash = null,
  speakPrompt = false,
}) => {
  if (!word) {
    return <div className="flashcard">Loading...</div>;
  }

  const isKanji = isKanjiCategory(word.categoryKey);
  const isKana = word.categoryKey === 'hiragana' || word.categoryKey === 'katakana';
  const variants = isKanji ? getKanjiVariants(word) : [];
  const activeIdx =
    isKanji && typeof activeKanjiVariantIndex === 'number'
      ? Math.min(activeKanjiVariantIndex, Math.max(0, variants.length - 1))
      : 0;
  const showVariantUi = isKanji && variants.length > 0;
  const canCycle = Boolean(onCycleKanjiVariant) && variants.length > 1;

  const kanjiLookupUrl =
    isKanji && word.foreign
      ? `${ROMAJIDESU_KANJI_BASE}${encodeURIComponent(word.foreign)}`
      : null;

  const wiktionaryLookupUrl =
    WIKTIONARY_LOOKUP_LANGUAGES.has(currentLanguage) && word.foreign
      ? englishWiktionaryUrl(word.foreign)
      : null;

  const scoreAnimClass = scoreFlash?.visible
    ? `flashcard--score-in ${scoreFlash.pass ? 'flashcard--score-pass' : 'flashcard--score-fail'}`
    : '';
  const cardClass = scoreAnimClass ? `flashcard ${scoreAnimClass}` : 'flashcard';

  const scoreChip = scoreFlash?.visible ? (
    <div className="score-flash" role="status" aria-live="polite">
      <span className="score-flash-value">
        {typeof scoreFlash.score === 'number' ? Math.round(scoreFlash.score) : scoreFlash.pass ? 100 : 0}
      </span>
      <span className="score-flash-label">{scoreFlash.pass ? 'pass' : 'try again'}</span>
    </div>
  ) : speakPrompt ? (
    <div className="score-flash score-flash--speak">
      <span className="score-flash-label score-flash-speak-text">speak</span>
    </div>
  ) : null;

  return (
    <div className={cardClass} key={scoreFlash?.visible ? `s-${scoreFlash.id}` : undefined}>
      <div className="content">
        {showVariantUi ? (
          <div
            className={canCycle ? 'kanji-variant-block kanji-variant-block--clickable' : 'kanji-variant-block'}
            onClick={canCycle ? onCycleKanjiVariant : undefined}
            onKeyDown={
              canCycle
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onCycleKanjiVariant();
                    }
                  }
                : undefined
            }
            role={canCycle ? 'button' : undefined}
            tabIndex={canCycle ? 0 : undefined}
            title={
              canCycle ? 'Click to switch which reading you are practicing' : undefined
            }
          >
            <p className="english kanji-variant-row">
              {variants.map((v, i) => (
                <span
                  key={`en-${i}`}
                  className={`variant-chip ${i === activeIdx ? 'variant-chip--active' : 'variant-chip--muted'}`}
                >
                  {v.english || '—'}
                </span>
              ))}
            </p>
            <h2 className={isKanji ? 'foreign kanji-foreign' : 'foreign'}>
              {kanjiLookupUrl ? (
                <a
                  href={kanjiLookupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="foreign-kanji-link"
                  title="Look up this kanji on RomajiDesu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {word.foreign}
                </a>
              ) : (
                word.foreign
              )}
            </h2>
            <p className="phonetic kanji-variant-row">
              {variants.map((v, i) => (
                <span
                  key={`kana-${i}`}
                  className={`variant-chip variant-chip--kana ${i === activeIdx ? 'variant-chip--active' : 'variant-chip--muted'}`}
                >
                  {v.kana || '—'}
                </span>
              ))}
            </p>
            {word.readingRomaji && (
              <p className="phonetic-latin kanji-variant-row">
                {variants.map((v, i) => (
                  <span
                    key={`ro-${i}`}
                    className={`variant-chip variant-chip--romaji ${i === activeIdx ? 'variant-chip--active' : 'variant-chip--muted'}`}
                  >
                    {v.romaji || '—'}
                  </span>
                ))}
              </p>
            )}
            {scoreChip}
          </div>
        ) : (
          <>
            {isKana ? (
              <>
                <h2 className="foreign kana-pair">
                  <span
                    className="kana-symbol"
                  >
                    {word.kanaHiragana || word.foreign}
                  </span>
                  <span
                    className="kana-symbol"
                  >
                    {word.kanaKatakana || word.foreign}
                  </span>
                </h2>
                <p className="phonetic kana-romaji">{word.english}</p>
                {scoreChip}
              </>
            ) : (
              <>
                <p className="english">{word.english}</p>
                <h2 className={isKanji ? 'foreign kanji-foreign' : 'foreign'}>
                  {kanjiLookupUrl ? (
                    <a
                      href={kanjiLookupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="foreign-kanji-link"
                      title="Look up this kanji on RomajiDesu"
                    >
                      {word.foreign}
                    </a>
                  ) : wiktionaryLookupUrl ? (
                    <a
                      href={wiktionaryLookupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="foreign-kanji-link"
                      title="Open in English Wiktionary (pronunciation and definitions)"
                    >
                      {word.foreign}
                    </a>
                  ) : (
                    word.foreign
                  )}
                </h2>
                <p className="phonetic">{word.phonetic}</p>
                {word.readingRomaji && (
                  <p className="phonetic-latin">{word.readingRomaji}</p>
                )}
                {scoreChip}
              </>
            )}
          </>
        )}
      </div>
      <div className="card-eq">
        <SpectrumBars height={56} />
      </div>
    </div>
  );
};

export default Flashcard;
